/**
 * The frame's copy of the impersonation session.
 *
 * Lives outside React because it is needed before the tree renders (the boot
 * handshake) and inside request closures (the token getter the client calls
 * on every platform request). The host is the source of truth; this module
 * only caches the last session it was handed and asks again when the token
 * runs low.
 */

import {
  type FrameMessage,
  IMPERSONATION_FRAME_NAME,
  type ImpersonationSession,
  parseHostMessage,
} from "../_libs/protocol";
import { FRAME_REFRESH_LEEWAY_MS, isTokenFresh } from "../_libs/token-expiry";

/** How long the frame waits for the host to answer a session request. */
const HOST_RESPONSE_TIMEOUT_MS = 10_000;

let session: ImpersonationSession | null = null;
let inFlightRequest: Promise<ImpersonationSession> | null = null;
const listeners = new Set<() => void>();

/**
 * Whether this document is the member portal embedded by the admin
 * impersonation overlay. Only that frame carries the marker name, so a
 * portal opened in its own tab, or embedded anywhere else, never waits on a
 * host that will not answer.
 */
export function isImpersonationFrame(): boolean {
  return (
    typeof window !== "undefined" &&
    window.parent !== window &&
    window.name === IMPERSONATION_FRAME_NAME
  );
}

export function getImpersonationSession(): ImpersonationSession | null {
  return session;
}

export function subscribeImpersonationSession(
  onChange: () => void,
): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function setSession(next: ImpersonationSession): void {
  session = next;
  for (const notify of listeners) notify();
}

function postToHost(message: FrameMessage): void {
  window.parent.postMessage(message, window.location.origin);
}

/** How often the request is repeated until the host answers. */
const HOST_REQUEST_RETRY_MS = 1_000;

/**
 * Ask the host for the current session and wait for its answer. Only a
 * same-origin message from the parent window is accepted, and only one that
 * parses as a host message; everything else is ignored while the wait
 * continues. The request is re-sent every second until answered, so a frame
 * whose first message lands before the host has attached its listener still
 * gets through.
 */
export function requestSessionFromHost(): Promise<ImpersonationSession> {
  return new Promise((resolve, reject) => {
    const host = window.parent;
    const origin = window.location.origin;

    const cleanup = () => {
      clearTimeout(timer);
      clearInterval(retry);
      window.removeEventListener("message", onMessage);
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("The impersonation host did not answer in time."));
    }, HOST_RESPONSE_TIMEOUT_MS);

    const retry = setInterval(() => {
      postToHost({ type: "impersonation:request-session" });
    }, HOST_REQUEST_RETRY_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== host) return;
      const message = parseHostMessage(event.data);
      if (!message) return;

      cleanup();
      if (message.type === "impersonation:session") {
        setSession(message.session);
        resolve(message.session);
        return;
      }
      reject(new Error(message.message));
    };

    window.addEventListener("message", onMessage);
    postToHost({ type: "impersonation:request-session" });
  });
}

/**
 * The bearer token every platform request in the frame carries. Returns the
 * cached token while it is fresh; otherwise asks the host once (concurrent
 * callers share the same request) and returns the new token.
 */
export async function getImpersonationToken(): Promise<string | null> {
  const current = session;
  if (
    current &&
    isTokenFresh(current.expiresAt, Date.now(), FRAME_REFRESH_LEEWAY_MS)
  ) {
    return current.token;
  }

  inFlightRequest ??= requestSessionFromHost().finally(() => {
    inFlightRequest = null;
  });
  const fresh = await inFlightRequest;
  return fresh.token;
}

/** Tell the host to close the impersonation overlay. */
export function exitImpersonation(): void {
  postToHost({ type: "impersonation:exit" });
}
